import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Assessment,
  Assignment,
  BarChart as BarChartIcon,
  Build,
  Business,
  Campaign,
  Close,
  Delete,
  Description,
  Download,
  DragIndicator,
  Edit,
  FilterList,
  Flight,
  Groups,
  Inventory2,
  LocalGasStation,
  Notes,
  Opacity,
  Person,
  PrecisionManufacturing,
  Preview,
  Repeat,
  ReportProblem,
  Route,
  Save,
  Search,
  Speed,
  Summarize,
  SwapHoriz,
  TableChart,
  ViewColumn
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { useToast } from '../../contexts/ToastContext';
import ReportSectionFiltersDialog from './ReportSectionFiltersDialog';
import ReportVisualizeDialog from './ReportVisualizeDialog';
import ReportPreviewDialog from './ReportPreviewDialog';
import DueReportPanel from './DueReportPanel';
import FuelConsumptionReportPanel from './FuelConsumptionReportPanel';
import OilConsumptionReportPanel from './OilConsumptionReportPanel';
import FlyingReportPanel from './FlyingReportPanel';
import EsrReportPanel from './EsrReportPanel';
import EnginePowerCheckReportPanel from './EnginePowerCheckReportPanel';
import DefectReportPanel from './DefectReportPanel';
import MelReleaseReportPanel from './MelReleaseReportPanel';
import CrsReportPanel from './CrsReportPanel';
import RepetitiveDefectReportPanel from './RepetitiveDefectReportPanel';
import ComponentReplacementReportPanel from './ComponentReplacementReportPanel';
import AdsbReportPanel from './AdsbReportPanel';
import AmpScheduleReportPanel from './AmpScheduleReportPanel';
import AircraftLifecycleReportPanel from './AircraftLifecycleReportPanel';
import ComponentReportPanel from './ComponentReportPanel';
import AircraftWiseStockReportPanel from './AircraftWiseStockReportPanel';
import ItemIssueHistoryReportPanel from './ItemIssueHistoryReportPanel';
import LoanInOutReportPanel from './LoanInOutReportPanel';
import AgainstPoInOutReportPanel from './AgainstPoInOutReportPanel';
import ShelfLifeExpiryReportPanel from './ShelfLifeExpiryReportPanel';
import StoreWiseStockReportPanel from './StoreWiseStockReportPanel';
import SalesReportPanel from './SalesReportPanel';
import PilotReportPanel from './PilotReportPanel';
import {
  downloadDraftReportCsv,
  downloadDraftReportPdf,
  downloadSavedReportCsv,
  downloadSavedReportPdf,
  downloadSavedReportPdfWithVisualization,
  fetchDraftReportPreviewData,
  fetchSavedReportData,
  resolveAxiosErrorMessage
} from '../../utils/reportCenterApi';
import { formatPageFilterSummary } from './filterLabelUtils';
import ColumnManagerDialog from './ColumnManagerDialog';
import {
  formatColumnSelectionSummary,
  mergeSectionConfigsIntoPages,
  pageColumnsToSectionConfig,
  sectionConfigToPageUpdates
} from './columnConfigUtils';

const SECTION_ICONS = {
  business: Business,
  person: Person,
  flight: Flight,
  route: Route,
  groups: Groups,
  notes: Notes,
  summarize: Summarize,
  table: TableChart,
  build: Build,
  inventory: Inventory2
};

const REPORT_CENTER_WRITE_ROLES = [
  ROLES.SPECTATOR,
  ROLES.SUPER_ADMIN,
  ROLES.CA_MANAGER,
  ROLES.CA_TECHNICAL_OFFICER,
  ROLES.CAMO_QM_MANAGER,
  ROLES.CAMO_QM_OFFICER,
  ROLES.MRO_MANAGER
];

const REPORT_NAV_ICONS = {
  custom: Summarize,
  due: Build,
  fuel: LocalGasStation,
  oil: Opacity,
  flying: Flight,
  esr: Assessment,
  epc: Speed,
  defect: ReportProblem,
  'mel-release': Assignment,
  crs: Description,
  'repetitive-defect': Repeat,
  'component-replacement': SwapHoriz,
  adsb: Campaign,
  'amp-schedule': Build,
  'aircraft-lifecycle': PrecisionManufacturing,
  component: PrecisionManufacturing,
  'aircraft-stock': Inventory2,
  'item-issue-history': Assignment,
  'loan-in-out': SwapHoriz,
  'against-po-in-out': Description,
  'shelf-life-expiry': Build,
  'store-wise-stock': Inventory2,
  'bonded-store-stock': Inventory2,
  'pol-store-stock': Inventory2,
  'gse-stock': Inventory2,
  'tool-store-stock': Inventory2,
  'quarantine-store': Inventory2,
  'scrap-store': Inventory2,
  sales: Flight,
  pilot: Person
};

const REPORT_NAV_GROUPS = [
  {
    category: 'Report Builder',
    items: [
      {
        id: 'custom',
        label: 'Custom Reports',
        description: 'Build reports from any data table'
      }
    ]
  },
  {
    category: 'Maintenance',
    items: [
      {
        id: 'due',
        label: 'Due Report',
        description: 'Past due and upcoming maintenance items'
      },
      {
        id: 'adsb',
        label: 'AD/SB Report',
        description: 'Airworthiness directives and service bulletins'
      },
      {
        id: 'amp-schedule',
        label: 'AMP Schedule Report',
        description: 'AMP schedules with list and detail fields'
      },
      {
        id: 'aircraft-lifecycle',
        label: 'Aircraft Life-cycle Report',
        description: 'Assemblies and components with detail fields'
      },
      {
        id: 'component',
        label: 'Component Report',
        description: 'Aircraft component list with detail fields'
      },
      {
        id: 'component-replacement',
        label: 'Component Replacement',
        description: 'Installed and removed components by date'
      }
    ]
  },
  {
    category: 'Technical Monitoring',
    items: [
      {
        id: 'fuel',
        label: 'Fuel Consumption',
        description: 'Fuel usage by aircraft and sector'
      },
      {
        id: 'oil',
        label: 'Oil Consumption',
        description: 'Oil usage by aircraft and sector'
      },
      {
        id: 'flying',
        label: 'Flying Report',
        description: 'Sector flying hours, landings, and fuel totals'
      },
      {
        id: 'esr',
        label: 'ESR Report',
        description: 'Engineering Statistics Report (fleet, engines, defects, MEL)'
      },
      {
        id: 'epc',
        label: 'Engine Power Check (EPC)',
        description: 'Engine performance margin analysis'
      },
      {
        id: 'defect',
        label: 'Defect Report',
        description: 'Technical log book defect entries'
      },
      {
        id: 'mel-release',
        label: 'MEL Release Report',
        description: 'Aircraft sectors released under MEL'
      },
      {
        id: 'crs',
        label: 'CRS Report',
        description: 'Issued CRS certificates for selected aircraft'
      },
      {
        id: 'repetitive-defect',
        label: 'Repetitive Defects',
        description: 'Recurring defect patterns and occurrences'
      }
    ]
  },
  {
    category: 'Store',
    items: [
      {
        id: 'aircraft-stock',
        label: 'Aircraft-wise Stock Report',
        description: 'Stock summary and detail by aircraft'
      },
      {
        id: 'item-issue-history',
        label: 'Item Issue Record (History)',
        description: 'Store issuance history and issued item lines'
      },
      {
        id: 'loan-in-out',
        label: 'Loan IN/OUT Record',
        description: 'Loan-out issuances and loan-in receiving'
      },
      {
        id: 'against-po-in-out',
        label: 'Against PO IN/OUT Record',
        description: 'Purchase orders and Against PO receiving'
      },
      {
        id: 'shelf-life-expiry',
        label: 'Shelf-Life Expiry / Expired Items',
        description: 'Due report for expired and expiring stock'
      },
      {
        id: 'store-wise-stock',
        label: 'Store-wise Stock Report',
        description: 'Stock summary and detail by store type'
      },
      {
        id: 'bonded-store-stock',
        label: 'Bonded Store Stock Report',
        description: 'Bonded store inventory summary and detail'
      },
      {
        id: 'pol-store-stock',
        label: 'POL Store Stock Report',
        description: 'POL store inventory summary and detail'
      },
      {
        id: 'gse-stock',
        label: 'GSE Stock Report',
        description: 'Ground support equipment stock across stores'
      },
      {
        id: 'tool-store-stock',
        label: 'Tool Store Stock Report',
        description: 'Tool store inventory summary and detail'
      },
      {
        id: 'quarantine-store',
        label: 'Quarantine Store Report',
        description: 'Quarantine stock — Serviceable / UN S / Calibrated'
      },
      {
        id: 'scrap-store',
        label: 'Scrap Store Report',
        description: 'Scrap (Scrape) store inventory summary and detail'
      }
    ]
  },
  {
    category: 'Operations & Crew',
    items: [
      {
        id: 'sales',
        label: 'Flight Entry Report',
        description: 'Charter trips, sectors, and flight activity'
      },
      {
        id: 'pilot',
        label: 'Pilot Report',
        description: 'Pilot compliance, duties, and FDTL'
      }
    ]
  }
];

const REPORT_NAV_ITEMS = REPORT_NAV_GROUPS.flatMap((group) => group.items);

const REPORT_NAV_IDS = new Set(REPORT_NAV_ITEMS.map((item) => item.id));

function reportNavIcon(reportId) {
  const Icon = REPORT_NAV_ICONS[reportId] || TableChart;
  return <Icon fontSize="small" />;
}

function isDataTableSection(section) {
  return section?.sectionType === 'data-table' || String(section?.sectionKey || '').startsWith('table:');
}

function sectionNeedsFilterDialog(section) {
  if (!section) return false;
  const hasFilters = (section.filters || []).length > 0;
  const hasSortOptions = (section.sortOptions || []).length > 0;
  if (isDataTableSection(section)) {
    return hasFilters || hasSortOptions;
  }
  return hasFilters;
}

function createClientInstanceId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `page-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function pagesPayload(selectedPages) {
  return selectedPages
    .filter((p) => p && p.sectionKey)
    .map((p, index) => {
    const page = {
      instanceId: p.instanceId || createClientInstanceId(),
      sectionKey: p.sectionKey,
      order: index
    };
    if (p.label) page.label = p.label;
    if (p.filters && Object.keys(p.filters).length > 0) page.filters = p.filters;
    if (Array.isArray(p.columns) && p.columns.length > 0) page.columns = p.columns;
    if (p.showTitle === false) page.showTitle = false;
    return page;
  });
}

function formatPageSummary(page, meta) {
  if (meta?.sectionKey === 'notes-section' && page.filters?.notesText) {
    const text = String(page.filters.notesText);
    return text.length > 80 ? `${text.slice(0, 80)}…` : text;
  }
  const isTable = isDataTableSection(meta);
  if (!sectionNeedsFilterDialog(meta)) return meta?.description || '';

  const parts = [];
  const filterParts = formatPageFilterSummary(page, meta);
  if (filterParts.length) {
    parts.push(`Filters: ${filterParts.join(', ')}`);
  }

  if (isTable && page.filters) {
    const sortBy = page.filters.sortBy || meta?.defaultSort?.by;
    const sortOrder = page.filters.sortOrder || meta?.defaultSort?.order || 'asc';
    if (sortBy && (meta?.sortOptions || []).length > 0) {
      const sortDef = meta.sortOptions.find((s) => s.key === sortBy);
      const dir = sortOrder === 'desc' ? 'descending' : 'ascending';
      parts.push(`Sort: ${sortDef?.label || sortBy} (${dir})`);
    }
  }
  if (isTable) {
    const columnSummary = formatColumnSelectionSummary(page, meta);
    const total = (meta?.columns || []).length;
    if (columnSummary && columnSummary !== `${total} columns`) {
      parts.push(columnSummary);
    }
    return parts.length ? parts.join(' · ') : 'All rows and columns';
  }
  return parts.length ? parts.join(' · ') : meta?.description || '';
}

function sectionIcon(iconKey) {
  const Icon = SECTION_ICONS[iconKey] || TableChart;
  return <Icon fontSize="small" />;
}

function pdfFilename(reportName) {
  const base = String(reportName || 'report')
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'report';
  return `${base}.pdf`;
}

function csvFilename(reportName) {
  return pdfFilename(reportName).replace(/\.pdf$/i, '.csv');
}

function todayDateValue() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function toDateInputValue(value) {
  if (!value) return todayDateValue();
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return todayDateValue();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

const ReportCenter = () => {
  const { user } = useAuth();
  const canWrite = REPORT_CENTER_WRITE_ROLES.includes(user?.role);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { reportType } = useParams();
  const selectedReportId = REPORT_NAV_IDS.has(reportType) ? reportType : null;

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [operators, setOperators] = useState([]);
  const [aircraftOptions, setAircraftOptions] = useState([]);
  const [sections, setSections] = useState([]);
  const [reports, setReports] = useState([]);

  const [profileForm, setProfileForm] = useState({ operatorId: '', displayName: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [reportName, setReportName] = useState('');
  const [selectedAircraftIds, setSelectedAircraftIds] = useState([]);
  const [selectedReportDate, setSelectedReportDate] = useState(todayDateValue);
  const [selectedDateFrom, setSelectedDateFrom] = useState('');
  const [selectedDateTo, setSelectedDateTo] = useState('');
  const [selectedPages, setSelectedPages] = useState([]);
  const [savingReport, setSavingReport] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewReportData, setPreviewReportData] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [visualizeDialogOpen, setVisualizeDialogOpen] = useState(false);
  const [visualizeData, setVisualizeData] = useState(null);
  const [visualizeTitle, setVisualizeTitle] = useState('');
  const [visualizeSource, setVisualizeSource] = useState(null); // { type: 'saved'|'draft', reportId?, reportDate?, dateFrom?, dateTo? }
  const [visualizing, setVisualizing] = useState(false);
  const [sectionSearch, setSectionSearch] = useState('');
  const [filterDialogSection, setFilterDialogSection] = useState(null);
  const [filterDialogMode, setFilterDialogMode] = useState('add');
  const [filterEditIndex, setFilterEditIndex] = useState(null);
  const [pendingFilters, setPendingFilters] = useState({});
  const [reportRunDates, setReportRunDates] = useState({});
  // Optional run-time date range per saved report: { [reportId]: { dateFrom, dateTo } }
  const [reportRunDateRanges, setReportRunDateRanges] = useState({});
  const [previewSavedReport, setPreviewSavedReport] = useState(null);
  const [previewSavedPages, setPreviewSavedPages] = useState([]);
  const [columnEditIndex, setColumnEditIndex] = useState(null);
  const [columnEditConfig, setColumnEditConfig] = useState(null);
  const [savingLayout, setSavingLayout] = useState(false);

  const defaultDisplayName = useMemo(
    () => `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
    [user]
  );

  const sectionMap = useMemo(
    () => Object.fromEntries(sections.map((s) => [s.sectionKey, s])),
    [sections]
  );

  const aircraftLabelById = useMemo(
    () => Object.fromEntries(aircraftOptions.map((a) => [String(a.value), a.label])),
    [aircraftOptions]
  );

  const formatAircraftLabels = useCallback(
    (ids) => (ids || [])
      .map((id) => aircraftLabelById[String(id)] || String(id))
      .join(', '),
    [aircraftLabelById]
  );

  const groupedSections = useMemo(() => {
    const term = sectionSearch.trim().toLowerCase();
    const filtered = sections.filter((s) => {
      if (!term) return true;
      return (
        s.name.toLowerCase().includes(term)
        || (s.description || '').toLowerCase().includes(term)
        || (s.category || '').toLowerCase().includes(term)
      );
    });
    const groups = {};
    filtered.forEach((s) => {
      const cat = s.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(s);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [sections, sectionSearch]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, sectionsRes, reportsRes, operatorsRes, aircraftRes] = await Promise.all([
        axios.get('/api/report-center/profile'),
        axios.get('/api/report-center/sections'),
        axios.get('/api/report-center/reports'),
        axios.get('/api/report-center/operators'),
        axios.get('/api/report-center/filter-options/aircraft')
      ]);

      setHasProfile(Boolean(profileRes.data.hasProfile));
      setProfile(profileRes.data.profile || null);
      setSections(sectionsRes.data.sections || []);
      setReports(reportsRes.data.reports || []);
      setOperators(operatorsRes.data || []);
      setAircraftOptions(aircraftRes.data.options || []);

      if (profileRes.data.profile) {
        setProfileForm({
          operatorId: profileRes.data.profile.operatorId || '',
          displayName: profileRes.data.profile.displayName || defaultDisplayName
        });
      } else {
        setProfileForm({
          operatorId: '',
          displayName: defaultDisplayName
        });
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load Report Center', 'error');
    } finally {
      setLoading(false);
    }
  }, [defaultDisplayName, showToast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (reportType && !REPORT_NAV_IDS.has(reportType)) {
      navigate('/report-center', { replace: true });
    }
  }, [reportType, navigate]);

  useEffect(() => {
    setReportRunDates((prev) => {
      const next = { ...prev };
      reports.forEach((report) => {
        if (!next[report._id]) {
          next[report._id] = toDateInputValue(report.reportDate);
        }
      });
      return next;
    });
  }, [reports]);

  const handleSaveProfile = async () => {
    if (!profileForm.operatorId || !profileForm.displayName.trim()) {
      showToast('Select an operator and enter your display name', 'warning');
      return;
    }
    setSavingProfile(true);
    try {
      const res = await axios.put('/api/report-center/profile', profileForm);
      setProfile(res.data.profile);
      setHasProfile(true);
      setProfileDialogOpen(false);
      showToast('Profile saved', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save profile', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const openNewReportBuilder = () => {
    setEditingReport(null);
    setReportName('');
    setSelectedAircraftIds([]);
    setSelectedReportDate(todayDateValue());
    setSelectedDateFrom('');
    setSelectedDateTo('');
    setSelectedPages([]);
    setBuilderOpen(true);
  };

  const openEditReportBuilder = (report) => {
    setEditingReport(report);
    setReportName(report.name);
    setSelectedAircraftIds((report.aircraftIds || []).map((id) => String(id)));
    setSelectedReportDate(toDateInputValue(report.reportDate));
    setSelectedDateFrom('');
    setSelectedDateTo('');
    const sortedPages = [...report.pages].sort((a, b) => a.order - b.order);
    const validPages = sortedPages.filter((p) => p?.sectionKey && sectionMap[p.sectionKey]);
    const removedCount = sortedPages.length - validPages.length;
    if (removedCount > 0) {
      showToast(
        `${removedCount} section${removedCount !== 1 ? 's were' : ' was'} removed because ${removedCount !== 1 ? 'they are' : 'it is'} no longer available in the catalog`,
        'warning'
      );
    }
    setSelectedPages(
      validPages.map((p, index) => ({
        ...p,
        instanceId: p.instanceId || createClientInstanceId(),
        order: index,
        filters: p.filters || undefined
      }))
    );
    setBuilderOpen(true);
  };

  const closeBuilder = () => {
    setBuilderOpen(false);
    setEditingReport(null);
    setReportName('');
    setSelectedAircraftIds([]);
    setSelectedReportDate(todayDateValue());
    setSelectedDateFrom('');
    setSelectedDateTo('');
    setSelectedPages([]);
    setDragIndex(null);
    setSectionSearch('');
    setFilterDialogSection(null);
  };

  const commitAddSection = (sectionKey, filters = {}) => {
    const meta = sectionMap[sectionKey];

    setSelectedPages((prev) => [
      ...prev,
      {
        instanceId: createClientInstanceId(),
        sectionKey,
        order: prev.length,
        label: meta?.name || sectionKey,
        filters: Object.keys(filters).length ? filters : undefined
      }
    ]);
    setFilterDialogSection(null);
  };

  const addSection = (sectionKey) => {
    const meta = sectionMap[sectionKey];
    if (sectionNeedsFilterDialog(meta)) {
      setFilterDialogMode('add');
      setFilterEditIndex(null);
      setPendingFilters({});
      setFilterDialogSection(meta);
      return;
    }
    commitAddSection(sectionKey);
  };

  const openFilterEditor = (index) => {
    const page = selectedPages[index];
    const meta = sectionMap[page.sectionKey];
    if (!sectionNeedsFilterDialog(meta)) return;
    setFilterDialogMode('edit');
    setFilterEditIndex(index);
    setPendingFilters(page.filters || {});
    setFilterDialogSection(meta);
  };

  const handleFilterDialogConfirm = ({ filters }) => {
    if (filterDialogMode === 'edit' && filterEditIndex !== null) {
      setSelectedPages((prev) => prev.map((p, i) => (
        i === filterEditIndex ? {
          ...p,
          filters: Object.keys(filters || {}).length ? filters : undefined
        } : p
      )));
      setFilterDialogSection(null);
      setFilterEditIndex(null);
      setPendingFilters({});
      return;
    }
    if (filterDialogSection) {
      commitAddSection(filterDialogSection.sectionKey, filters);
    }
    setFilterDialogSection(null);
    setFilterEditIndex(null);
    setPendingFilters({});
  };

  const openColumnEditor = (index) => {
    const page = selectedPages[index];
    const meta = sectionMap[page.sectionKey];
    if (!isDataTableSection(meta)) return;
    setColumnEditIndex(index);
    setColumnEditConfig(pageColumnsToSectionConfig(page, {
      ...meta,
      title: meta.name,
      columns: meta.columns
    }));
  };

  const closeColumnEditor = () => {
    setColumnEditIndex(null);
    setColumnEditConfig(null);
  };

  const handleColumnDialogApply = (patch) => {
    if (columnEditIndex === null) return;
    const meta = sectionMap[selectedPages[columnEditIndex].sectionKey];
    const config = { ...columnEditConfig, ...patch };
    const updates = sectionConfigToPageUpdates(config, {
      ...meta,
      title: meta.name,
      columns: meta.columns
    });
    setSelectedPages((prev) => prev.map((p, i) => {
      if (i !== columnEditIndex) return p;
      const next = { ...p };
      if (updates.columns?.length) next.columns = updates.columns;
      else delete next.columns;
      if (updates.label) next.label = updates.label;
      else if (patch.titleOverride === '') delete next.label;
      if (updates.showTitle === false) next.showTitle = false;
      else delete next.showTitle;
      return next;
    }));
    closeColumnEditor();
  };

  const removeSection = (index) => {
    setSelectedPages((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((p, i) => ({ ...p, order: i }))
    );
  };

  const moveSection = (index, direction) => {
    setSelectedPages((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next.map((p, i) => ({ ...p, order: i }));
    });
  };

  const handleCanvasDragStart = (index) => {
    setDragIndex(index);
  };

  const handleCanvasDragOver = (event, index) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    setSelectedPages((prev) => {
      if (dragIndex < 0 || dragIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      if (!moved?.sectionKey) return prev;
      next.splice(index, 0, moved);
      return next.map((p, i) => ({ ...p, order: i }));
    });
    setDragIndex(index);
  };

  const handleCanvasDragEnd = () => {
    setDragIndex(null);
  };

  const handleSaveReport = async () => {
    if (!reportName.trim()) {
      showToast('Enter a report name', 'warning');
      return;
    }
    const validPages = selectedPages.filter((p) => p?.sectionKey);
    if (validPages.length === 0) {
      showToast('Add at least one page section', 'warning');
      return;
    }
    if (validPages.length !== selectedPages.length) {
      setSelectedPages(validPages.map((p, i) => ({ ...p, order: i })));
      showToast('Removed an invalid empty section — please review and save again', 'warning');
      return;
    }

    setSavingReport(true);
    try {
      const payload = {
        name: reportName.trim(),
        pages: pagesPayload(selectedPages),
        aircraftIds: selectedAircraftIds,
        reportDate: selectedReportDate || todayDateValue()
      };

      if (editingReport) {
        await axios.put(`/api/report-center/reports/${editingReport._id}`, payload);
        showToast('Report updated', 'success');
      } else {
        await axios.post('/api/report-center/reports', payload);
        showToast('Report created', 'success');
      }

      closeBuilder();
      const reportsRes = await axios.get('/api/report-center/reports');
      const nextReports = reportsRes.data.reports || [];
      setReports(nextReports);
      if (editingReport) {
        setReportRunDates((prev) => ({
          ...prev,
          [editingReport._id]: selectedReportDate
        }));
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save report', 'error');
    } finally {
      setSavingReport(false);
    }
  };

  const closePreviewDialog = () => {
    setPreviewDialogOpen(false);
    setPreviewReportData(null);
    setPreviewTitle('');
    setPreviewSavedReport(null);
    setPreviewSavedPages([]);
  };

  const handleSavePreviewLayout = async (sectionConfigs) => {
    const mergedPages = mergeSectionConfigsIntoPages(
      previewSavedPages,
      sectionConfigs,
      sectionMap
    ).map((page, index) => ({ ...page, order: index }));

    // Draft / builder preview — persist field selection back into the open builder
    if (!previewSavedReport) {
      setSelectedPages(mergedPages);
      setPreviewSavedPages(mergedPages);
      showToast('Field selection saved to draft — click Save report to keep it', 'success');
      return;
    }

    setSavingLayout(true);
    try {
      await axios.put(`/api/report-center/reports/${previewSavedReport._id}`, {
        name: previewSavedReport.name,
        pages: pagesPayload(mergedPages),
        aircraftIds: previewSavedReport.aircraftIds || [],
        reportDate: resolveRunReportDate(previewSavedReport)
      });
      showToast('Report layout saved', 'success');
      const reportsRes = await axios.get('/api/report-center/reports');
      setReports(reportsRes.data.reports || []);
      setPreviewSavedPages(mergedPages);
      setPreviewSavedReport((prev) => (
        prev ? { ...prev, pages: mergedPages } : prev
      ));
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save layout', 'error');
    } finally {
      setSavingLayout(false);
    }
  };

  const closeVisualizeDialog = () => {
    setVisualizeDialogOpen(false);
    setVisualizeData(null);
    setVisualizeTitle('');
    setVisualizeSource(null);
  };

  const resolveRunReportDate = useCallback(
    (report) => reportRunDates[report._id] || toDateInputValue(report.reportDate),
    [reportRunDates]
  );

  const resolveRunDateRange = useCallback(
    (report) => ({
      dateFrom: reportRunDateRanges[report._id]?.dateFrom || '',
      dateTo: reportRunDateRanges[report._id]?.dateTo || ''
    }),
    [reportRunDateRanges]
  );

  const setRunDateRangeField = (reportId, field, value) => {
    setReportRunDateRanges((prev) => ({
      ...prev,
      [reportId]: { ...(prev[reportId] || {}), [field]: value }
    }));
  };

  const getDateRangeError = (dateFrom, dateTo) => (
    dateFrom && dateTo && dateFrom > dateTo ? 'Date From must be on or before Date To' : ''
  );

  /** Shows a warning toast and returns true when the optional range is invalid. */
  const rejectInvalidDateRange = (dateFrom, dateTo) => {
    const message = getDateRangeError(dateFrom, dateTo);
    if (message) showToast(message, 'warning');
    return Boolean(message);
  };

  const handleVisualizeSaved = async (report) => {
    const runDate = resolveRunReportDate(report);
    const { dateFrom, dateTo } = resolveRunDateRange(report);
    if (rejectInvalidDateRange(dateFrom, dateTo)) return;
    setVisualizing(true);
    try {
      const data = await fetchSavedReportData(report._id, runDate, { dateFrom, dateTo });
      setVisualizeTitle(report.name);
      setVisualizeData(data);
      setVisualizeSource({
        type: 'saved',
        reportId: report._id,
        reportDate: runDate,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined
      });
      setVisualizeDialogOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err), 'error');
    } finally {
      setVisualizing(false);
    }
  };

  const handleVisualizeDraft = async () => {
    if (selectedPages.length === 0) {
      showToast('Add sections to visualize', 'warning');
      return;
    }
    if (rejectInvalidDateRange(selectedDateFrom, selectedDateTo)) return;
    setVisualizing(true);
    try {
      const name = reportName.trim() || 'Draft Report';
      const data = await fetchDraftReportPreviewData({
        reportName: name,
        pages: pagesPayload(selectedPages),
        aircraftIds: selectedAircraftIds,
        dateFrom: selectedDateFrom || undefined,
        dateTo: selectedDateTo || undefined,
        reportDate: selectedReportDate || todayDateValue()
      });
      setVisualizeTitle(name);
      setVisualizeData(data);
      setVisualizeSource({ type: 'draft' });
      setVisualizeDialogOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err), 'error');
    } finally {
      setVisualizing(false);
    }
  };

  const handlePreviewDraft = async () => {
    if (selectedPages.length === 0) {
      showToast('Add sections to preview', 'warning');
      return;
    }
    if (rejectInvalidDateRange(selectedDateFrom, selectedDateTo)) return;
    setPreviewing(true);
    try {
      const reportData = await fetchDraftReportPreviewData({
        reportName: reportName.trim() || 'Draft Report',
        pages: pagesPayload(selectedPages),
        aircraftIds: selectedAircraftIds,
        dateFrom: selectedDateFrom || undefined,
        dateTo: selectedDateTo || undefined,
        reportDate: selectedReportDate || todayDateValue()
      });
      setPreviewTitle(reportName.trim() || 'Draft Report');
      setPreviewReportData(reportData);
      setPreviewSavedReport(null);
      setPreviewSavedPages(selectedPages);
      setPreviewDialogOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err), 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handlePreviewSaved = async (report) => {
    const { dateFrom, dateTo } = resolveRunDateRange(report);
    if (rejectInvalidDateRange(dateFrom, dateTo)) return;
    setPreviewing(true);
    try {
      const reportData = await fetchSavedReportData(report._id, resolveRunReportDate(report), {
        dateFrom,
        dateTo
      });
      setPreviewTitle(report.name);
      setPreviewReportData({
        ...reportData,
        reportName: reportData.reportName || report.name
      });
      setPreviewSavedReport(report);
      setPreviewSavedPages(
        [...(report.pages || [])].sort((a, b) => a.order - b.order)
      );
      setPreviewDialogOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Could not open preview', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleDownloadSaved = async (report) => {
    const { dateFrom, dateTo } = resolveRunDateRange(report);
    if (rejectInvalidDateRange(dateFrom, dateTo)) return;
    setDownloading(true);
    try {
      await downloadSavedReportPdf(
        report._id,
        pdfFilename(report.name),
        resolveRunReportDate(report),
        { dateFrom, dateTo }
      );
      showToast('PDF downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err), 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadSavedCsv = async (report) => {
    const { dateFrom, dateTo } = resolveRunDateRange(report);
    if (rejectInvalidDateRange(dateFrom, dateTo)) return;
    setDownloadingCsv(true);
    try {
      await downloadSavedReportCsv(
        report._id,
        pdfFilename(report.name),
        resolveRunReportDate(report),
        { dateFrom, dateTo }
      );
      showToast('CSV downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err), 'error');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleDownloadDraft = async () => {
    if (selectedPages.length === 0) {
      showToast('Add sections to download', 'warning');
      return;
    }
    if (rejectInvalidDateRange(selectedDateFrom, selectedDateTo)) return;
    setDownloading(true);
    try {
      const name = reportName.trim() || 'Draft Report';
      await downloadDraftReportPdf(
        {
          reportName: name,
          pages: pagesPayload(selectedPages),
          aircraftIds: selectedAircraftIds,
          dateFrom: selectedDateFrom || undefined,
          dateTo: selectedDateTo || undefined,
          reportDate: selectedReportDate || todayDateValue()
        },
        pdfFilename(name)
      );
      showToast('PDF downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err), 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadDraftCsv = async () => {
    if (selectedPages.length === 0) {
      showToast('Add sections to download', 'warning');
      return;
    }
    if (rejectInvalidDateRange(selectedDateFrom, selectedDateTo)) return;
    setDownloadingCsv(true);
    try {
      const name = reportName.trim() || 'Draft Report';
      await downloadDraftReportCsv(
        {
          reportName: name,
          pages: pagesPayload(selectedPages),
          aircraftIds: selectedAircraftIds,
          dateFrom: selectedDateFrom || undefined,
          dateTo: selectedDateTo || undefined,
          reportDate: selectedReportDate || todayDateValue()
        },
        csvFilename(name)
      );
      showToast('CSV downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err), 'error');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleDownloadWithVisualization = async (visualizations) => {
    if (!visualizeSource) return;
    setDownloading(true);
    try {
      if (visualizeSource.type === 'saved') {
        await downloadSavedReportPdfWithVisualization(visualizeSource.reportId, {
          filename: pdfFilename(`${visualizeTitle}_with_visualizations`),
          reportDate: visualizeSource.reportDate || undefined,
          dateFrom: visualizeSource.dateFrom,
          dateTo: visualizeSource.dateTo,
          visualizations
        });
      } else {
        const name = reportName.trim() || visualizeTitle || 'Draft Report';
        await downloadDraftReportPdf(
          {
            reportName: name,
            pages: pagesPayload(selectedPages),
            aircraftIds: selectedAircraftIds,
            dateFrom: selectedDateFrom || undefined,
            dateTo: selectedDateTo || undefined,
            reportDate: selectedReportDate || todayDateValue(),
            visualizations
          },
          pdfFilename(`${name}_with_visualizations`)
        );
      }
      showToast('PDF with visualizations downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err), 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Delete this report recipe?')) return;
    try {
      await axios.delete(`/api/report-center/reports/${reportId}`);
      setReports((prev) => prev.filter((r) => r._id !== reportId));
      showToast('Report deleted', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Delete failed', 'error');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!hasProfile) {
    return (
      <Box maxWidth={560} mx="auto" mt={4}>
        <Typography variant="h4" gutterBottom>
          Report Center
        </Typography>
        <Typography color="text.secondary" paragraph>
          Set up your operator and display name to use Report Center. Your name appears as “Prepared by” on reports.
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Stack spacing={3}>
            <FormControl fullWidth required>
              <InputLabel>Operator</InputLabel>
              <Select
                value={profileForm.operatorId}
                label="Operator"
                onChange={(e) => setProfileForm((f) => ({ ...f, operatorId: e.target.value }))}
              >
                {operators.map((op) => (
                  <MenuItem key={op._id} value={op._id}>
                    {op.name}{op.code ? ` (${op.code})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {operators.length === 0 && (
              <Alert severity="info">
                No operators found. Add operators in Master Data Management first.
              </Alert>
            )}
            <TextField
              label="Your name on reports"
              value={profileForm.displayName}
              onChange={(e) => setProfileForm((f) => ({ ...f, displayName: e.target.value }))}
              fullWidth
              required
              helperText="Shown as “Prepared by” on your reports"
            />
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSaveProfile}
              disabled={savingProfile || operators.length === 0}
            >
              {savingProfile ? 'Saving…' : 'Save & continue'}
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Report Center
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<Business />}
              label={profile?.operator?.name || 'Operator'}
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<Person />}
              label={profile?.displayName}
              size="small"
              variant="outlined"
            />
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button startIcon={<Edit />} onClick={() => setProfileDialogOpen(true)}>
            Edit profile
          </Button>
          {canWrite && selectedReportId === 'custom' && (
            <Button variant="contained" startIcon={<Add />} onClick={openNewReportBuilder}>
              New report
            </Button>
          )}
        </Stack>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Paper
            variant="outlined"
            sx={{
              position: { md: 'sticky' },
              top: { md: 88 },
              maxHeight: { md: 'calc(100vh - 112px)' },
              overflow: 'auto'
            }}
          >
            {REPORT_NAV_GROUPS.map((group, groupIndex) => (
              <Box key={group.category}>
                {groupIndex > 0 && (
                  <Box sx={{ mx: 2, borderTop: 1, borderColor: 'divider' }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    px: 2,
                    py: 1,
                    bgcolor: 'action.hover',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: 'text.secondary'
                  }}
                >
                  {group.category}
                </Typography>
                <List dense disablePadding>
                  {group.items.map((item) => (
                    <ListItem key={item.id} disablePadding>
                      <ListItemButton
                        selected={selectedReportId === item.id}
                        onClick={() => navigate(`/report-center/${item.id}`)}
                        sx={{ py: 1, alignItems: 'flex-start' }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            mt: 0.25,
                            color: selectedReportId === item.id ? 'inherit' : 'primary.main'
                          }}
                        >
                          {reportNavIcon(item.id)}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          secondary={item.description}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                          secondaryTypographyProps={{
                            variant: 'caption',
                            sx: { display: 'block', lineHeight: 1.35, mt: 0.25 }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={9}>
          {!selectedReportId && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Select a report
              </Typography>
              <Typography color="text.secondary">
                Choose a report from the list to view and run it.
              </Typography>
            </Paper>
          )}

          {selectedReportId === 'due' && (
            <DueReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'fuel' && (
            <FuelConsumptionReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'oil' && (
            <OilConsumptionReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'flying' && (
            <FlyingReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'esr' && (
            <EsrReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'epc' && (
            <EnginePowerCheckReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'defect' && (
            <DefectReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'mel-release' && (
            <MelReleaseReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'crs' && (
            <CrsReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'repetitive-defect' && (
            <RepetitiveDefectReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'component-replacement' && (
            <ComponentReplacementReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'adsb' && (
            <AdsbReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'amp-schedule' && (
            <AmpScheduleReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'aircraft-lifecycle' && (
            <AircraftLifecycleReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'component' && (
            <ComponentReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'aircraft-stock' && (
            <AircraftWiseStockReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'item-issue-history' && (
            <ItemIssueHistoryReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'loan-in-out' && (
            <LoanInOutReportPanel />
          )}

          {selectedReportId === 'against-po-in-out' && (
            <AgainstPoInOutReportPanel />
          )}

          {selectedReportId === 'shelf-life-expiry' && (
            <ShelfLifeExpiryReportPanel />
          )}

          {selectedReportId === 'store-wise-stock' && (
            <StoreWiseStockReportPanel />
          )}

          {selectedReportId === 'bonded-store-stock' && (
            <StoreWiseStockReportPanel
              reportKind="BONDED"
              lockedStoreType="BONDED"
              title="Bonded Store Stock Report"
              description="Inventory held in the Bonded store — summary totals and line-level detail."
            />
          )}

          {selectedReportId === 'pol-store-stock' && (
            <StoreWiseStockReportPanel
              reportKind="POL"
              lockedStoreType="POL"
              title="POL Store Stock Report"
              description="Inventory held in the POL store — summary totals and line-level detail."
            />
          )}

          {selectedReportId === 'gse-stock' && (
            <StoreWiseStockReportPanel
              reportKind="GSE"
              title="GSE Stock Report"
              description="Ground support equipment (GSE) stock across all stores — summary by store and line detail."
            />
          )}

          {selectedReportId === 'tool-store-stock' && (
            <StoreWiseStockReportPanel
              reportKind="TOOLS"
              lockedStoreType="TOOLS"
              title="Tool Store Stock Report"
              description="Inventory held in the Tool store — summary totals and line-level detail."
            />
          )}

          {selectedReportId === 'quarantine-store' && (
            <StoreWiseStockReportPanel
              reportKind="QUARANTINE"
              lockedStoreType="QUARANTINE"
              title="Quarantine Store Report"
              description="Quarantine store stock — filter by Serviceable, Unserviceable (UN S), or Calibrated tools."
            />
          )}

          {selectedReportId === 'scrap-store' && (
            <StoreWiseStockReportPanel
              reportKind="SCRAPE"
              lockedStoreType="SCRAPE"
              title="Scrap Store Report"
              description="Inventory held in the Scrap (Scrape) store — summary totals and line-level detail."
            />
          )}

          {selectedReportId === 'sales' && (
            <SalesReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'pilot' && (
            <PilotReportPanel
              aircraftOptions={aircraftOptions}
              aircraftLabelById={aircraftLabelById}
            />
          )}

          {selectedReportId === 'custom' && (reports.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No reports yet
          </Typography>
          <Typography color="text.secondary" paragraph>
            Build reports from any app table — aircraft, CAMO, maintenance, store, loan, operations, parts, master data, admin, and more. Each section supports the same filters as the live screen.
          </Typography>
          {canWrite && (
            <Button variant="contained" startIcon={<Add />} onClick={openNewReportBuilder}>
              Create your first report
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {reports.map((report) => (
            <Grid item xs={12} sm={6} md={4} key={report._id}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {report.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {report.pageCount} section{report.pageCount !== 1 ? 's' : ''}
                  </Typography>
                  {report.aircraftIds?.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Aircrafts: {formatAircraftLabels(report.aircraftIds)}
                    </Typography>
                  )}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      label="Date from"
                      type="date"
                      size="small"
                      fullWidth
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      value={resolveRunDateRange(report).dateFrom}
                      onChange={(e) => setRunDateRangeField(report._id, 'dateFrom', e.target.value)}
                      error={Boolean(getDateRangeError(
                        resolveRunDateRange(report).dateFrom,
                        resolveRunDateRange(report).dateTo
                      ))}
                    />
                    <TextField
                      label="Date to"
                      type="date"
                      size="small"
                      fullWidth
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      value={resolveRunDateRange(report).dateTo}
                      onChange={(e) => setRunDateRangeField(report._id, 'dateTo', e.target.value)}
                      error={Boolean(getDateRangeError(
                        resolveRunDateRange(report).dateFrom,
                        resolveRunDateRange(report).dateTo
                      ))}
                    />
                  </Stack>
                  <Typography
                    variant="caption"
                    color={getDateRangeError(
                      resolveRunDateRange(report).dateFrom,
                      resolveRunDateRange(report).dateTo
                    ) ? 'error' : 'text.secondary'}
                    display="block"
                  >
                    {getDateRangeError(
                      resolveRunDateRange(report).dateFrom,
                      resolveRunDateRange(report).dateTo
                    ) || 'Optional — leave blank for all records'}
                  </Typography>
                  <TextField
                    label="Report date"
                    type="date"
                    size="small"
                    fullWidth
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                    value={reportRunDates[report._id] || toDateInputValue(report.reportDate)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setReportRunDates((prev) => ({ ...prev, [report._id]: value }));
                    }}
                    helperText="Used for preview, download, and visualization"
                  />
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {[...report.pages]
                      .sort((a, b) => a.order - b.order)
                      .map((p) => {
                        const meta = sectionMap[p.sectionKey];
                        const duplicateSection = report.pages.filter(
                          (page) => page.sectionKey === p.sectionKey
                        ).length > 1;
                        const chipLabel = p.label || meta?.name || p.sectionKey;
                        const chip = (
                          <Chip
                            key={`${report._id}-${p.instanceId || p.sectionKey}`}
                            label={chipLabel}
                            size="small"
                          />
                        );
                        if (!duplicateSection && !formatPageSummary(p, meta)) {
                          return chip;
                        }
                        return (
                          <Tooltip
                            key={`${report._id}-${p.instanceId || p.sectionKey}`}
                            title={formatPageSummary(p, meta) || chipLabel}
                          >
                            {chip}
                          </Tooltip>
                        );
                      })}
                  </Stack>
                </CardContent>
                <CardActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  <Button
                    size="small"
                    startIcon={<BarChartIcon />}
                    onClick={() => handleVisualizeSaved(report)}
                    disabled={previewing || downloading || downloadingCsv || visualizing}
                  >
                    Visualize
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Preview />}
                    onClick={() => handlePreviewSaved(report)}
                    disabled={previewing || downloading || downloadingCsv || visualizing}
                  >
                    Preview
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={() => handleDownloadSaved(report)}
                    disabled={previewing || downloading || downloadingCsv || visualizing}
                  >
                    PDF
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={() => handleDownloadSavedCsv(report)}
                    disabled={previewing || downloading || downloadingCsv || visualizing}
                  >
                    CSV
                  </Button>
                  {canWrite && (
                    <Button size="small" startIcon={<Edit />} onClick={() => openEditReportBuilder(report)}>
                      Edit
                    </Button>
                  )}
                  {canWrite && (
                    <IconButton size="small" color="error" onClick={() => handleDeleteReport(report._id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ))}

        </Grid>
      </Grid>

      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit profile</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Operator</InputLabel>
              <Select
                value={profileForm.operatorId}
                label="Operator"
                onChange={(e) => setProfileForm((f) => ({ ...f, operatorId: e.target.value }))}
              >
                {operators.map((op) => (
                  <MenuItem key={op._id} value={op._id}>
                    {op.name}{op.code ? ` (${op.code})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Your name on reports"
              value={profileForm.displayName}
              onChange={(e) => setProfileForm((f) => ({ ...f, displayName: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveProfile} disabled={savingProfile}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={builderOpen} onClose={closeBuilder} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingReport ? 'Edit report' : 'New report'}
          <IconButton
            onClick={closeBuilder}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Report name"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="e.g. Weekly Ops Pack"
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="report-aircrafts-label">Aircrafts</InputLabel>
            <Select
              labelId="report-aircrafts-label"
              multiple
              value={selectedAircraftIds}
              label="Aircrafts"
              onChange={(e) => {
                const value = e.target.value;
                setSelectedAircraftIds(typeof value === 'string' ? value.split(',') : value);
              }}
              renderValue={(selected) => {
                if (!selected.length) return 'All aircraft';
                return selected.map((id) => aircraftLabelById[id] || id).join(', ');
              }}
            >
              {aircraftOptions.map((ac) => (
                <MenuItem key={ac.value} value={ac.value}>
                  {ac.label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
              Optional — limit sections to selected aircraft and auto-include full Aircraft Data
            </Typography>
          </FormControl>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
            <TextField
              label="Date from"
              type="date"
              value={selectedDateFrom}
              onChange={(e) => setSelectedDateFrom(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={Boolean(getDateRangeError(selectedDateFrom, selectedDateTo))}
              helperText="Optional — leave blank for all records"
            />
            <TextField
              label="Date to"
              type="date"
              value={selectedDateTo}
              onChange={(e) => setSelectedDateTo(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={Boolean(getDateRangeError(selectedDateFrom, selectedDateTo))}
              helperText={
                getDateRangeError(selectedDateFrom, selectedDateTo)
                || 'Optional — applied to supported sections at preview / download (not saved with the report)'
              }
            />
          </Stack>

          <TextField
            label="Report date"
            type="date"
            value={selectedReportDate}
            onChange={(e) => setSelectedReportDate(e.target.value)}
            fullWidth
            sx={{ mb: 3 }}
            InputLabelProps={{ shrink: true }}
            helperText="Shown as the report date on the Prepared By section"
          />

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Available sections ({sections.length})
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="Search tables and sections…"
                value={sectionSearch}
                onChange={(e) => setSectionSearch(e.target.value)}
                InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} /> }}
                sx={{ mb: 1 }}
              />
              <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
                {groupedSections.length === 0 ? (
                  <Box p={2}>
                    <Typography variant="body2" color="text.secondary">
                      No sections match your search.
                    </Typography>
                  </Box>
                ) : (
                  groupedSections.map(([category, categorySections]) => (
                    <Box key={category}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          px: 2,
                          py: 0.75,
                          bgcolor: 'action.hover',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em'
                        }}
                      >
                        {category}
                      </Typography>
                      <List dense disablePadding>
                        {categorySections.map((section) => (
                            <ListItem key={section.sectionKey} disablePadding>
                              <ListItemButton
                                onClick={() => addSection(section.sectionKey)}
                              >
                                <Box mr={1} display="flex" alignItems="center">
                                  {sectionIcon(section.icon)}
                                </Box>
                                <ListItemText
                                  primary={section.name}
                                  secondary={section.description}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                                <Add fontSize="small" color="primary" />
                              </ListItemButton>
                            </ListItem>
                          ))}
                      </List>
                    </Box>
                  ))
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" gutterBottom>
                Your report pages — drag to reorder
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  minHeight: 360,
                  p: 1,
                  bgcolor: selectedPages.length ? 'background.paper' : 'action.hover'
                }}
              >
                {selectedPages.length === 0 ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minHeight={340}
                    color="text.secondary"
                  >
                    Click sections on the left to add pages
                  </Box>
                ) : (
                  <List disablePadding>
                    {selectedPages.map((page, index) => {
                      const meta = sectionMap[page.sectionKey];
                      return (
                        <ListItem
                          key={page.instanceId || `${page.sectionKey}-${index}`}
                          draggable
                          onDragStart={() => handleCanvasDragStart(index)}
                          onDragOver={(e) => handleCanvasDragOver(e, index)}
                          onDragEnd={handleCanvasDragEnd}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            bgcolor: dragIndex === index ? 'action.selected' : 'background.paper',
                            cursor: 'grab'
                          }}
                          secondaryAction={
                            <Stack direction="row" alignItems="center">
                              {isDataTableSection(meta) && (
                                <Tooltip title="Manage columns">
                                  <IconButton size="small" onClick={() => openColumnEditor(index)}>
                                    <ViewColumn fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {sectionNeedsFilterDialog(meta) && (
                                <Tooltip title="Edit configuration">
                                  <IconButton size="small" onClick={() => openFilterEditor(index)}>
                                    <FilterList fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Move up">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveSection(index, -1)}
                                    disabled={index === 0}
                                  >
                                    <ArrowUpward fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Move down">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveSection(index, 1)}
                                    disabled={index === selectedPages.length - 1}
                                  >
                                    <ArrowDownward fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <IconButton size="small" color="error" onClick={() => removeSection(index)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          }
                        >
                          <DragIndicator sx={{ mr: 1, color: 'text.disabled' }} />
                          <ListItemText
                            primary={`${index + 1}. ${page.label || meta?.name || page.sectionKey}`}
                            secondary={formatPageSummary(page, meta)}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<BarChartIcon />}
            onClick={handleVisualizeDraft}
            disabled={previewing || downloading || downloadingCsv || visualizing || selectedPages.length === 0}
          >
            Visualize
          </Button>
          <Button
            startIcon={<Preview />}
            onClick={handlePreviewDraft}
            disabled={previewing || downloading || downloadingCsv || visualizing || selectedPages.length === 0}
          >
            Preview
          </Button>
          <Button
            startIcon={<Download />}
            onClick={handleDownloadDraft}
            disabled={previewing || downloading || downloadingCsv || visualizing || selectedPages.length === 0}
          >
            Download PDF
          </Button>
          <Button
            startIcon={<Download />}
            onClick={handleDownloadDraftCsv}
            disabled={previewing || downloading || downloadingCsv || visualizing || selectedPages.length === 0}
          >
            Download CSV
          </Button>
          <Box flexGrow={1} />
          <Button onClick={closeBuilder}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveReport}
            disabled={savingReport}
          >
            {savingReport ? 'Saving…' : 'Save report'}
          </Button>
        </DialogActions>
      </Dialog>

      <ReportVisualizeDialog
        open={visualizeDialogOpen}
        title={visualizeTitle}
        data={visualizeData}
        downloading={downloading}
        onClose={closeVisualizeDialog}
        onDownloadPdfWithVisualization={handleDownloadWithVisualization}
      />

      <ReportPreviewDialog
        open={previewDialogOpen}
        title={`Preview — ${previewTitle}`}
        reportData={previewReportData}
        savedPages={previewSavedPages}
        onSaveLayout={handleSavePreviewLayout}
        saveLayoutLabel={previewSavedReport ? 'Save layout to report' : 'Save field selection'}
        savingLayout={savingLayout}
        onClose={closePreviewDialog}
      />

      {columnEditIndex !== null && columnEditConfig && (
        <ColumnManagerDialog
          open
          section={{
            title: sectionMap[selectedPages[columnEditIndex]?.sectionKey]?.name,
            name: sectionMap[selectedPages[columnEditIndex]?.sectionKey]?.name,
            columns: sectionMap[selectedPages[columnEditIndex]?.sectionKey]?.columns || []
          }}
          config={columnEditConfig}
          onClose={closeColumnEditor}
          onApply={handleColumnDialogApply}
        />
      )}

      <ReportSectionFiltersDialog
        open={Boolean(filterDialogSection)}
        section={filterDialogSection}
        initialFilters={filterDialogMode === 'edit' ? pendingFilters : {}}
        onClose={() => setFilterDialogSection(null)}
        onConfirm={handleFilterDialogConfirm}
        confirmLabel={filterDialogMode === 'edit' ? 'Save' : 'Add section'}
      />

      {(previewing || downloading || downloadingCsv || visualizing) && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgcolor="rgba(0,0,0,0.2)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={9999}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default ReportCenter;
